import React, { useEffect, useState, useCallback } from 'react';
import { Tabs, Row, Col, Pagination, Empty } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomNavbar from '../components/Navbar';
import RequestCard from '../components/RequestCard';
import api from '../api/axios';
import { useProfile } from '../context/ProfileContext';


import Swal from 'sweetalert2';

const Request = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [activeKey, setActiveKey] = useState('1');
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [profiles, setProfiles] = useState([]);
	const [totalProfiles, setTotalProfiles] = useState(0);
	const [perPage, setPerPage] = useState(10);
	const { profileId } = useProfile();

	useEffect(() => {
		if (location.pathname === '/requests/sent') {
			setActiveKey('1');
		} else if (location.pathname === '/requests/received') {
			setActiveKey('2');
		} else if (location.pathname === '/requests/accepted') {
			setActiveKey('3');
		} else if (location.pathname === '/requests/rejected') {
			setActiveKey('4');
		} else if (location.pathname === '/requests/cancelled') {
			setActiveKey('5');
		}
	}, [location.pathname]);

	const fetchProfiles = useCallback(async (key, page, limit) => {
		const typeMap = {
			'1': 'sent',
			'2': 'received',
			'3': 'accepted',
			'4': 'rejected',
			'5': 'cancelled',
		};
		const type = typeMap[key];
		try {
			const response = await api.post(
				'/invites/list',
				{
					type,
					page,
					limit,
					profile_id: profileId
				}
			);
			setProfiles(response.data.data.data);
			setTotalProfiles(response.data.data.total);
			setPerPage(response.data.data.per_page);
		} catch (error) {
			console.error('Error fetching profiles:', error);
		}
	}, [profileId]);

	useEffect(() => {
		fetchProfiles(activeKey, currentPage, pageSize);
	}, [activeKey, currentPage, pageSize, fetchProfiles]);

	const handleTabChange = (key) => {
		if (key === '1') {
			navigate('/requests/sent');
		} else if (key === '2') {
			navigate('/requests/received');
		} else if (key === '3') {
			navigate('/requests/accepted');
		} else if (key === '4') {
			navigate('/requests/rejected');
		} else if (key === '5') {
			navigate('/requests/cancelled');
		}
	};

	const handlePageChange = (page, pageSize) => {
		setCurrentPage(page);
		setPageSize(pageSize);
	};

	const handleAcceptRequest = async (inviteId) => {
		try {
			const response = await api.put(
				`/invites/${inviteId}/accept`,
				{ profile_id: profileId }
			);

			if (response.data?.status === 'success') {
				Swal.fire(
					'Request Accepted!',
					'The request has been accepted.',
					'success'
				);
			} else {
				Swal.fire(
					'Error',
					'There was an error accepting the request.',
					'error'
				);
			}
		} catch (error) {
			console.error('Error accepting request:', error);
			Swal.fire(
				'Error',
				'There was an error accepting the request.',
				'error'
			);
		} finally {
			fetchProfiles(activeKey, currentPage, pageSize);
		}
	};

	const handleRejectRequest = async (inviteId) => {
		try {
			const response = await api.put(
				`/invites/${inviteId}/reject`,
				{ profile_id: profileId }
			);

			if (response.data?.status === 'success') {
				Swal.fire(
					'Request Rejected!',
					'The request has been rejected.',
					'success'
				);
			} else {
				Swal.fire(
					'Error',
					'There was an error rejecting the request.',
					'error'
				);
			}
		} catch (error) {
			console.error('Error rejecting request:', error);
			Swal.fire(
				'Error',
				'There was an error rejecting the request.',
				'error'
			);
		} finally {
			fetchProfiles(activeKey, currentPage, pageSize);
		}
	};

	const handleCancelRequest = async (inviteId) => {
		try {
			const response = await api.put(
				`/invites/${inviteId}/cancel`,
				{ profile_id: profileId }
			);

			if (response.data?.status === 'success') {
				Swal.fire(
					'Request Cancelled!',
					'The request has been cancelled.',
					'success'
				);
			} else {
				Swal.fire(
					'Error',
					'There was an error cancelling the request.',
					'error'
				);
			}
		} catch (error) {
			console.error('Error cancelling request:', error);
			Swal.fire(
				'Error',
				'There was an error cancelling the request.',
				'error'
			);
		} finally {
			fetchProfiles(activeKey, currentPage, pageSize);
		}
	};

	const renderProfiles = (profiles) => {
		if (profiles.length === 0) {
			return (
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
					<Empty description="No Data" />
				</div>
			);
		}

		return (
			<Row gutter={[16, 16]} className="mt-4">
				{profiles.map((profile) => (
					<Col key={profile.id} xs={24} sm={12} md={8} lg={6}>
						<RequestCard
							profile={profile}
							onAccept={handleAcceptRequest}
							onReject={handleRejectRequest}
							onCancel={handleCancelRequest}
							activeKey={activeKey}
						/>
					</Col>
				))}
			</Row>
		);
	};

	const tabItems = [
		{
			key: '1',
			label: <span style={{ fontFamily: 'Calibri, sans-serif', fontSize: '1.0rem', fontWeight: 'normal' }}>Sent Requests</span>,
			children: (
				<>
					{renderProfiles(profiles)}
					{totalProfiles > perPage && (
						<Pagination
							current={currentPage}
							pageSize={pageSize}
							total={totalProfiles}
							onChange={handlePageChange}
							style={{ textAlign: 'center', marginTop: '16px' }}
						/>
					)}
				</>
			),
		},
		{
			key: '2',
			label: <span style={{ fontFamily: 'Calibri, sans-serif', fontSize: '1.0rem', fontWeight: 'normal' }}>Received Requests</span>,
			children: (
				<>
					{renderProfiles(profiles)}
					{totalProfiles > perPage && (
						<Pagination
							current={currentPage}
							pageSize={pageSize}
							total={totalProfiles}
							onChange={handlePageChange}
							style={{ textAlign: 'center', marginTop: '16px' }}
						/>
					)}
				</>
			),
		},
		{
			key: '3',
			label: <span style={{ fontFamily: 'Calibri, sans-serif', fontSize: '1.0rem', fontWeight: 'normal' }}>Accepted Requests</span>,
			children: (
				<>
					{renderProfiles(profiles)}
					{totalProfiles > perPage && (
						<Pagination
							current={currentPage}
							pageSize={pageSize}
							total={totalProfiles}
							onChange={handlePageChange}
							style={{ textAlign: 'center', marginTop: '16px' }}
						/>
					)}
				</>
			),
		},
		{
			key: '4',
			label: <span style={{ fontFamily: 'Calibri, sans-serif', fontSize: '1.0rem', fontWeight: 'normal' }}>Rejected Requests</span>,
			children: (
				<>
					{renderProfiles(profiles)}
					{totalProfiles > perPage && (
						<Pagination
							current={currentPage}
							pageSize={pageSize}
							total={totalProfiles}
							onChange={handlePageChange}
							style={{ textAlign: 'center', marginTop: '16px' }}
						/>
					)}
				</>
			),
		},
		{
			key: '5',
			label: <span style={{ fontFamily: 'Calibri, sans-serif', fontSize: '1.0rem', fontWeight: 'normal' }}>Cancelled Requests</span>,
			children: (
				<>
					{renderProfiles(profiles)}
					{totalProfiles > perPage && (
						<Pagination
							current={currentPage}
							pageSize={pageSize}
							total={totalProfiles}
							onChange={handlePageChange}
							style={{ textAlign: 'center', marginTop: '16px' }}
						/>
					)}
				</>
			),
		},
	];

	useEffect(() => {
		const titles = {
			'/requests/sent': 'Sent Interests',
			'/requests/received': 'Received Interests',
			'/requests/accepted': 'Accepted Interests',
			'/requests/rejected': 'Rejected Interests',
			'/requests/cancelled': 'Cancelled Interests',
		};
		document.title = `${titles[location.pathname] ?? 'Interests'} | Oottan`;
	}, [location.pathname]);

	return (
		<div style={{ fontFamily: 'Calibri, sans-serif' }}>
			<CustomNavbar onNavbarReady={() => {}} />
			<div className="container py-5">
				<div style={{ marginTop: '15px' }}>
					<Tabs activeKey={activeKey} onChange={handleTabChange} centered items={tabItems} />
				</div>
			</div>
		</div>
	);
};

export default Request;